import { useSearch } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"
import type { RefObject } from "react"
import type { Client, CombinedError } from "urql"
import { useShallow } from "zustand/react/shallow"

import { PENDING_APPROVALS_ROUTE } from "../constants"
import type { SourceChangeApprovalSearchFilters } from "../stores/useSourceChangeSearchStore"
import { useSourceChangeSearchStore } from "../stores/useSourceChangeSearchStore"
import type { SourceChangeApprovalRow } from "../types"
import { toSourceChangeApprovalRows } from "../utils/sourceChange"

import type { SourceChangeApprovalProduct } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseSourceChangeListReturn {
  sourceChanges: SourceChangeApprovalRow[]
  isFetching: boolean
  error: CombinedError | Error | undefined
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
}

const ROUTE = "/app/dashboard/_layout/pending-approvals/_layout/source-change"

export const useSourceChangeList = (): UseSourceChangeListReturn => {
  const { taskViewScope } = useSearch({ from: PENDING_APPROVALS_ROUTE })

  const store = useSourceChangeSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )
  const { executeSearch: storeExecuteSearch, loadMore: storeLoadMore } = store

  const executeSearch = useCallback(
    (client: Client, params: SourceChangeApprovalSearchFilters, force?: boolean) => {
      storeExecuteSearch(client, { ...params, taskViewScope }, force)
    },
    [storeExecuteSearch, taskViewScope]
  )

  const loadMore = useCallback(
    (client: Client, params: SourceChangeApprovalSearchFilters) => {
      storeLoadMore(client, { ...params, taskViewScope })
    },
    [storeLoadMore, taskViewScope]
  )

  const {
    data: sourceChangeApprovals,
    isFetching,
    error,
    tableContainerRef,
    refetch,
    handleScroll,
  } = useListPageBase<SourceChangeApprovalProduct, SourceChangeApprovalSearchFilters>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
  })

  const sourceChanges = useMemo(() => toSourceChangeApprovalRows(sourceChangeApprovals), [sourceChangeApprovals])

  return {
    sourceChanges,
    isFetching,
    error,
    tableContainerRef,
    refetch,
    handleScroll,
  }
}
